import { gsap } from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

export default () => ({
    resizeObserver: null,
    resizeHandler: null,
    mobileLayerClickHandler: null,
    raf: null,
    mobileScrollTrigger: null,
    lineScrollTrigger: null,
    desktopVisibleScrollTrigger: null,
    mobilePillRevealTimer: null,
    activeMobileLayer: null,
    visibleBounds: {},
    linePathLength: 0,
    alignmentRun: 0,
    alignmentTransitionLock: 0,

    init() {
        this.$nextTick(() => this.setup());
    },

    setup() {
        this.resizeHandler = () => this.queueAlign();
        this.mobileLayerClickHandler = event => this.handleMobileLayerClick(event);

        window.addEventListener('resize', this.resizeHandler, { passive: true });
        this.getImage()?.addEventListener('click', this.mobileLayerClickHandler);

        if (window.ResizeObserver) {
            this.resizeObserver = new ResizeObserver(() => this.queueAlign());
            this.resizeObserver.observe(this.$el);
        }

        if (document.fonts?.ready) {
            document.fonts.ready.then(() => this.queueAlign());
        }

        this.getImageLayers().forEach(image => {
            if (!image.complete) {
                image.addEventListener('load', this.resizeHandler, { once: true });
            }
        });

        this.queueAlign();
    },

    destroy() {
        window.removeEventListener('resize', this.resizeHandler);
        this.getImage()?.removeEventListener('click', this.mobileLayerClickHandler);
        this.resizeObserver?.disconnect();
        this.killDesktopVisibleScrollTrigger();
        this.killMobileScrollTrigger();
        this.killLineScrollTrigger();
        this.resetLinePath();
        this.clearAlignmentTransitionLock();

        if (this.raf) {
            window.cancelAnimationFrame(this.raf);
        }
    },

    queueAlign() {
        if (this.raf) {
            window.cancelAnimationFrame(this.raf);
        }

        this.raf = window.requestAnimationFrame(() => this.alignLayers());
    },

    async alignLayers() {
        const run = ++this.alignmentRun;
        const images = this.getImageLayers();
        const copyLayers = this.getCopyLayers();
        const isMobile = this.isMobile();
        const shouldDisableTransitions = !isMobile;

        if (shouldDisableTransitions) {
            this.lockAlignmentTransitions();
        }

        try {
            this.resetLayerOffsets(images, copyLayers, isMobile);

            if (shouldDisableTransitions) {
                this.forceLayout();
            }

            this.setupLineScrollTrigger();
            this.setupDesktopVisibleScrollTrigger();

            if (isMobile) {
                this.setupMobileLayout(images, copyLayers);
                return;
            }

            this.resetMobileLayout(images, copyLayers);
            this.killMobileScrollTrigger();

            const pointerOneY = this.getPointerCenterY('1');
            const pointerThreeY = this.getPointerCenterY('3');

            if (!images.length || pointerOneY === null || pointerThreeY === null) {
                return;
            }

            const targetY = {
                1: pointerOneY,
                2: pointerOneY + ((pointerThreeY - pointerOneY) / 2),
                3: pointerThreeY,
            };

            await Promise.all(images.map(image => this.ensureVisibleBounds(image)));

            if (run !== this.alignmentRun || this.isMobile()) {
                return;
            }

            this.alignCopyLayers(copyLayers, targetY);

            images.forEach(image => {
                const layer = image.dataset.pillImgLayer;
                const target = targetY[layer];
                const ratio = this.getAnchorRatio(image);

                if (target === undefined) {
                    return;
                }

                const rect = image.getBoundingClientRect();
                const centerY = rect.top + (rect.height * ratio) - this.getLayerRevealOffsetY(image, rect);
                const offset = target - centerY;

                image.style.setProperty('--pill-layer-offset-y', `${offset}px`);
            });

            if (shouldDisableTransitions) {
                this.forceLayout();
            }
        } finally {
            if (shouldDisableTransitions) {
                this.unlockAlignmentTransitions();
            }
        }
    },

    resetLayerOffsets(images, copyLayers, disableCopyAdjust = false) {
        images.forEach(image => {
            image.style.setProperty('--pill-layer-offset-y', '0px');
        });

        copyLayers.forEach(layer => {
            layer.style.setProperty('--pill-copy-offset-y', '0px');

            if (disableCopyAdjust) {
                layer.style.setProperty('--pill-copy-render-adjust-y', '0px');
            } else {
                layer.style.removeProperty('--pill-copy-render-adjust-y');
            }
        });
    },

    setupMobileLayout(images, copyLayers) {
        this.setMobileLayerHeight(copyLayers);
        this.setupMobileScrollTrigger(images, copyLayers);
    },

    resetMobileLayout(images, copyLayers) {
        this.$el.style.removeProperty('--pill-layer-min-height');

        images.forEach(image => {
            image.classList.remove('pill__img__layer--active');
            image.style.removeProperty('opacity');
        });

        copyLayers.forEach(layer => {
            layer.classList.remove('pill__layer--active');
            layer.style.removeProperty('opacity');
            layer.style.removeProperty('pointer-events');
            layer.removeAttribute('aria-hidden');
        });

        this.activeMobileLayer = null;
    },

    setMobileLayerHeight(copyLayers) {
        if (!copyLayers.length) {
            return;
        }

        this.$el.style.removeProperty('--pill-layer-min-height');

        const maxHeight = Math.max(...copyLayers.map(layer => layer.offsetHeight));

        if (Number.isFinite(maxHeight) && maxHeight > 0) {
            this.$el.style.setProperty('--pill-layer-min-height', `${Math.ceil(maxHeight)}px`);
        }
    },

    setupMobileScrollTrigger(images, copyLayers) {
        if (!copyLayers.length) {
            this.killMobileScrollTrigger();
            return;
        }

        this.setActiveMobileLayer(this.getMobileLayerFromProgress(this.mobileScrollTrigger?.progress || 0), images, copyLayers);

        if (this.mobileScrollTrigger) {
            this.mobileScrollTrigger.refresh();
            return;
        }

        const sticky = this.getSticky();

        if (!sticky) {
            return;
        }

        this.mobileScrollTrigger = ScrollTrigger.create({
            trigger: sticky,
            start: 'top top',
            endTrigger: sticky,
            end: 'bottom bottom',
            markers: false,
            scrub: true,
            invalidateOnRefresh: true,
            onRefresh: self => {
                this.setMobileScrollProgress(self.progress, images, copyLayers);
            },
            onUpdate: self => {
                this.setMobileScrollProgress(self.progress, images, copyLayers);
            },
        });
    },

    setMobileScrollProgress(progress, images, copyLayers) {
        const clampedProgress = this.clampProgress(progress);

        this.setActiveMobileLayer(this.getMobileLayerFromProgress(clampedProgress), images, copyLayers);
    },

    handleMobileLayerClick(event) {
        if (!this.isMobile()) {
            return;
        }

        const layer = this.getMobileLayerFromPoint(event.clientX, event.clientY);

        if (!layer) {
            return;
        }

        event.preventDefault();
        this.scrollToMobileLayer(layer);
    },

    scrollToMobileLayer(layer) {
        if (!this.isMobile()) {
            return;
        }

        const targetProgress = this.getMobileProgressForLayer(layer);
        const scrollBounds = this.getMobileScrollBounds();

        this.setActiveMobileLayer(layer);

        if (!scrollBounds) {
            return;
        }

        const targetScroll = scrollBounds.start + ((scrollBounds.end - scrollBounds.start) * targetProgress);
        const maxScroll = typeof ScrollTrigger.maxScroll === 'function' ? ScrollTrigger.maxScroll(window) : document.documentElement.scrollHeight - window.innerHeight;
        const clampedScroll = Math.min(Math.max(targetScroll, 0), maxScroll);
        const immediate = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (window.lenis?.scrollTo) {
            window.lenis.scrollTo(clampedScroll, { immediate });
            return;
        }

        window.scrollTo({
            top: clampedScroll,
            behavior: immediate ? 'auto' : 'smooth',
        });
    },

    getMobileScrollBounds() {
        if (this.mobileScrollTrigger && Number.isFinite(this.mobileScrollTrigger.start) && Number.isFinite(this.mobileScrollTrigger.end)) {
            return {
                start: this.mobileScrollTrigger.start,
                end: this.mobileScrollTrigger.end,
            };
        }

        const sticky = this.getSticky();

        if (!sticky) {
            return null;
        }

        const rect = sticky.getBoundingClientRect();
        const start = window.scrollY + rect.top;
        const end = window.scrollY + rect.bottom - window.innerHeight;

        return {
            start,
            end: Math.max(start, end),
        };
    },

    getMobileProgressForLayer(layer) {
        const numericLayer = Math.min(Math.max(parseInt(layer, 10) || 1, 1), 3);

        return (numericLayer - 0.5) / 3;
    },

    getMobileLayerFromPoint(clientX, clientY) {
        const image = this.getImage();

        if (!image) {
            return null;
        }

        const localPoint = this.getLocalPoint(image, clientX, clientY);

        if (!localPoint) {
            return null;
        }

        const height = image.offsetHeight;

        if (!height || localPoint.x < 0 || localPoint.x > image.offsetWidth || localPoint.y < 0 || localPoint.y > height) {
            return null;
        }

        const progress = this.clampProgress(localPoint.y / height);

        return this.getMobileLayerFromProgress(progress);
    },

    getLocalPoint(element, clientX, clientY) {
        const rect = element.getBoundingClientRect();
        const width = element.offsetWidth;
        const height = element.offsetHeight;
        const style = window.getComputedStyle(element);
        const matrix = style.transform === 'none' ? new DOMMatrix() : new DOMMatrix(style.transform);
        const origin = this.getTransformOrigin(style.transformOrigin);
        const corners = [
            this.transformPoint(matrix, 0, 0, origin),
            this.transformPoint(matrix, width, 0, origin),
            this.transformPoint(matrix, width, height, origin),
            this.transformPoint(matrix, 0, height, origin),
        ];
        const minX = Math.min(...corners.map(point => point.x));
        const minY = Math.min(...corners.map(point => point.y));
        const baseX = rect.left - minX;
        const baseY = rect.top - minY;

        try {
            const inverse = matrix.inverse();
            const point = new DOMPoint(clientX - baseX - origin.x, clientY - baseY - origin.y).matrixTransform(inverse);

            return {
                x: point.x + origin.x,
                y: point.y + origin.y,
            };
        } catch {
            return null;
        }
    },

    transformPoint(matrix, x, y, origin) {
        const point = new DOMPoint(x - origin.x, y - origin.y).matrixTransform(matrix);

        return {
            x: point.x + origin.x,
            y: point.y + origin.y,
        };
    },

    getTransformOrigin(value) {
        const [x = '0', y = '0'] = value.split(' ');

        return {
            x: parseFloat(x) || 0,
            y: parseFloat(y) || 0,
        };
    },

    killMobileScrollTrigger() {
        if (this.mobileScrollTrigger) {
            this.mobileScrollTrigger.kill();
            this.mobileScrollTrigger = null;
        }
    },

    setupDesktopVisibleScrollTrigger() {
        const main = this.getMain();

        if (this.isMobile() || !main) {
            this.killDesktopVisibleScrollTrigger();
            main?.classList.remove('pill__main--visible');
            return;
        }

        if (this.desktopVisibleScrollTrigger) {
            this.desktopVisibleScrollTrigger.refresh();
            return;
        }

        this.desktopVisibleScrollTrigger = ScrollTrigger.create({
            trigger: this.$el,
            start: 'center 70%',
            end: 'bottom top',
            markers: false,
            toggleClass: {
                targets: main,
                className: 'pill__main--visible',
            },
        });
    },

    killDesktopVisibleScrollTrigger() {
        if (this.desktopVisibleScrollTrigger) {
            this.desktopVisibleScrollTrigger.kill();
            this.desktopVisibleScrollTrigger = null;
        }

        this.getMain()?.classList.remove('pill__main--visible');
    },

    setActiveMobileLayer(layer, images = this.getImageLayers(), copyLayers = this.getCopyLayers()) {
        const activeLayer = String(layer);

        if (this.activeMobileLayer === activeLayer) {
            return;
        }

        this.activeMobileLayer = activeLayer;

        copyLayers.forEach(copyLayer => {
            const isActive = copyLayer.dataset.pillCopyLayer === activeLayer;

            copyLayer.classList.toggle('pill__layer--active', isActive);
            copyLayer.toggleAttribute('aria-hidden', !isActive);
        });

        images.forEach(image => {
            image.classList.toggle('pill__img__layer--active', image.dataset.pillImgLayer === activeLayer);
        });
    },

    getMobileLayerFromProgress(progress) {
        const index = Math.min(Math.floor(this.clampProgress(progress) * 3), 2);

        return index + 1;
    },

    clampProgress(progress) {
        return Math.min(Math.max(progress, 0), 1);
    },

    setupLineScrollTrigger() {
        const trigger = this.getLineTrigger();

        if (!trigger || !this.$refs.linePath) {
            this.killLineScrollTrigger();
            this.resetLinePath();
            return;
        }

        this.setupLinePath();

        if (this.lineScrollTrigger) {
            if (this.lineScrollTrigger.trigger !== trigger) {
                this.killLineScrollTrigger();
            } else {
                this.lineScrollTrigger.refresh();
                return;
            }
        }

        this.lineScrollTrigger = ScrollTrigger.create({
            trigger,
            start: () => this.isMobile() ? 'top top' : 'top center',
            endTrigger: trigger,
            end: () => this.isMobile() ? 'bottom bottom' : 'bottom center',
            markers: false,
            scrub: true,
            invalidateOnRefresh: true,
            onRefresh: self => {
                this.setupLinePath();
                this.setLinePathProgress(self.progress);
                this.syncMobilePillReveal(self);
            },
            onUpdate: self => {
                this.setLinePathProgress(self.progress);
                this.syncMobilePillReveal(self);
            },
        });
    },

    killLineScrollTrigger() {
        if (this.lineScrollTrigger) {
            this.lineScrollTrigger.kill();
            this.lineScrollTrigger = null;
        }

        this.clearMobilePillRevealTimer();
        this.getMain()?.classList.remove('pill__main--mobile-pills-visible');
        this.getMain()?.classList.remove('pill__main--mobile-pills-revealed');
    },

    syncMobilePillReveal(scrollTrigger) {
        const main = this.getMain();

        if (!main) {
            return;
        }

        const isPastStart = typeof scrollTrigger?.scroll === 'function'
            ? scrollTrigger.scroll() >= scrollTrigger.start
            : Boolean(scrollTrigger?.isActive || scrollTrigger?.progress > 0);

        const shouldReveal = this.isMobile() && isPastStart;
        const wasVisible = main.classList.contains('pill__main--mobile-pills-visible');

        main.classList.toggle('pill__main--mobile-pills-visible', shouldReveal);

        if (!shouldReveal) {
            this.clearMobilePillRevealTimer();
            main.classList.remove('pill__main--mobile-pills-revealed');
            return;
        }

        if (wasVisible || this.mobilePillRevealTimer || main.classList.contains('pill__main--mobile-pills-revealed')) {
            return;
        }

        this.mobilePillRevealTimer = window.setTimeout(() => {
            this.mobilePillRevealTimer = null;
            main.classList.add('pill__main--mobile-pills-revealed');
        }, 1000);
    },

    clearMobilePillRevealTimer() {
        if (!this.mobilePillRevealTimer) {
            return;
        }

        window.clearTimeout(this.mobilePillRevealTimer);
        this.mobilePillRevealTimer = null;
    },

    lockAlignmentTransitions() {
        this.alignmentTransitionLock += 1;
        this.getMain()?.classList.add('pill__main--aligning');
    },

    unlockAlignmentTransitions() {
        this.alignmentTransitionLock = Math.max(this.alignmentTransitionLock - 1, 0);

        if (this.alignmentTransitionLock === 0) {
            this.getMain()?.classList.remove('pill__main--aligning');
        }
    },

    clearAlignmentTransitionLock() {
        this.alignmentTransitionLock = 0;
        this.getMain()?.classList.remove('pill__main--aligning');
    },

    forceLayout() {
        const main = this.getMain();

        if (main) {
            void main.offsetHeight;
        }
    },

    setupLinePath() {
        const path = this.$refs.linePath;

        if (!path || typeof path.getTotalLength !== 'function') {
            return;
        }

        this.linePathLength = path.getTotalLength();
        path.style.strokeDasharray = `${this.linePathLength} ${this.linePathLength}`;
        this.setLinePathProgress(this.lineScrollTrigger?.progress || 0);
        path.style.opacity = '1';
    },

    setLinePathProgress(progress) {
        const path = this.$refs.linePath;

        if (!path || !this.linePathLength) {
            return;
        }

        const clampedProgress = this.clampProgress(progress);
        path.style.strokeDashoffset = `${this.linePathLength * (1 - clampedProgress)}`;
    },

    resetLinePath() {
        const path = this.$refs.linePath;

        this.linePathLength = 0;

        if (!path) {
            return;
        }

        path.style.removeProperty('stroke-dasharray');
        path.style.removeProperty('stroke-dashoffset');
        path.style.removeProperty('opacity');
    },

    alignCopyLayers(copyLayers, targetY) {
        copyLayers.forEach(copyLayer => {
            const layer = copyLayer.dataset.pillCopyLayer;
            const target = targetY[layer];

            copyLayer.style.setProperty('--pill-copy-render-adjust-y', '0px');

            const pointerY = this.getPointerCenterY(layer);

            if (target === undefined || pointerY === null) {
                copyLayer.style.removeProperty('--pill-copy-render-adjust-y');
                return;
            }

            copyLayer.style.setProperty('--pill-copy-offset-y', `${target - pointerY}px`);
            copyLayer.style.removeProperty('--pill-copy-render-adjust-y');
        });
    },

    getPointerCenterY(layer) {
        const pointer = this.$el.querySelector(`[data-pill-pointer="${layer}"]`);

        if (!pointer) {
            return null;
        }

        const rect = pointer.getBoundingClientRect();

        return rect.top + (rect.height / 2);
    },

    getImageLayers() {
        return Array.from(this.$el.querySelectorAll('[data-pill-img-layer]'));
    },

    getCopyLayers() {
        return Array.from(this.$el.querySelectorAll('[data-pill-copy-layer]'));
    },

    getImage() {
        return this.$el.querySelector('.pill__img');
    },

    getSticky() {
        return this.$el.querySelector('.pill__sticky');
    },

    getMain() {
        return this.$el.querySelector('.pill__main');
    },

    getLineTrigger() {
        return this.isMobile() ? this.getSticky() : this.$el;
    },

    isMobile() {
        return Boolean(this.$store.app.isMobile);
    },

    async ensureVisibleBounds(image) {
        const layer = image.dataset.pillImgLayer;

        if (this.visibleBounds[layer] !== undefined) {
            return;
        }

        await this.waitForImage(image);

        this.visibleBounds[layer] = this.getVisibleBounds(image);
    },

    waitForImage(image) {
        if (image.complete && image.naturalWidth) {
            return Promise.resolve();
        }

        if (typeof image.decode === 'function') {
            return image.decode().catch(() => {});
        }

        return new Promise(resolve => {
            image.addEventListener('load', resolve, { once: true });
            image.addEventListener('error', resolve, { once: true });
        });
    },

    getAnchorRatio(image) {
        const layer = image.dataset.pillImgLayer;
        const bounds = this.visibleBounds[layer] ?? { min: 0, max: 1 };
        const value = window.getComputedStyle(image).getPropertyValue('--pill-layer-anchor-y').trim();
        const percent = value.endsWith('%') ? parseFloat(value) : 50;
        const anchor = Number.isFinite(percent) ? percent / 100 : 0.5;
        const clampedAnchor = Math.min(Math.max(anchor, 0), 1);

        return bounds.min + ((bounds.max - bounds.min) * clampedAnchor);
    },

    getLayerRevealOffsetY(image, rect = image.getBoundingClientRect()) {
        const value = window.getComputedStyle(image).getPropertyValue('--pill-layer-reveal-offset-y').trim();

        if (!value) {
            return 0;
        }

        if (value.endsWith('%')) {
            const percent = parseFloat(value);

            return Number.isFinite(percent) ? rect.height * (percent / 100) : 0;
        }

        const pixels = parseFloat(value);

        return Number.isFinite(pixels) ? pixels : 0;
    },

    getVisibleBounds(image) {
        if (!image.naturalWidth || !image.naturalHeight) {
            return { min: 0, max: 1 };
        }

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d', { willReadFrequently: true });

        if (!context) {
            return { min: 0, max: 1 };
        }

        try {
            canvas.width = image.naturalWidth;
            canvas.height = image.naturalHeight;
            context.drawImage(image, 0, 0);

            const { data, width, height } = context.getImageData(0, 0, canvas.width, canvas.height);
            let minY = height;
            let maxY = 0;

            for (let y = 0; y < height; y++) {
                for (let x = 0; x < width; x++) {
                    const alpha = data[((y * width + x) * 4) + 3];

                    if (alpha > 8) {
                        minY = Math.min(minY, y);
                        maxY = Math.max(maxY, y);
                    }
                }
            }

            if (minY > maxY) {
                return { min: 0, max: 1 };
            }

            return {
                min: minY / height,
                max: maxY / height,
            };
        } catch {
            return { min: 0, max: 1 };
        }
    },
});
